<template>
  <div
    class="microlearing-icon"
    @mouseover="isHovered = true"
    @mouseleave="isHovered = false"
    @click="handleClick(checkPointData)"
  >
    <EXTooltip
      v-if="isProcessing"
      :title="'Video is being processed and will be available in a moment'"
      placement="top"
      :delay="300"
    >
      <div class="processing-indicator">
        <Icon :name="'readiness_video_icon'" />
        <div class="processing-overlay">
          <div class="processing-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </EXTooltip>
    <div v-else-if="has_microlearning && !has_watched_video">
      <Icon :name="'readiness_video_icon'" />
    </div>
    <div v-else-if="has_microlearning && has_watched_video">
      <Icon :name="'watched_video'" />
    </div>
    <div v-else>
      <Icon :name="isHovered ? 'add_video' : 'no_microlearning'" />
    </div>
  </div>
</template>

<script setup>
import { defineProps, ref, computed, watch, nextTick } from "vue";
import Icon from "@components/icons/Icon.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";

const props = defineProps({
  has_microlearning: {
    type: Boolean,
    default: false,
    required: true,
  },
  has_watched_video: {
    type: Boolean,
    default: false,
  },
  checkPointData: {
    type: Object,
    default: null, // or 0, depending on your logic
  },
  onClick: {
    type: Function,
    default: () => {},
  },
});

const isHovered = ref(false);
const forceUpdate = ref(0);

// Check if video is being processed
const isProcessing = computed(() => {
  // Force reactivity by accessing forceUpdate
  forceUpdate.value;
  
  if (props.checkPointData && props.checkPointData.microlearning) {
    const microlearningContent = props.checkPointData.microlearning;
    return microlearningContent.includes("being processed") || 
           microlearningContent.includes("will be available in a moment") ||
           microlearningContent.includes("processing");
  }
  return false;
});

// Watch for changes in checkPointData and force re-evaluation
watch(
  () => props.checkPointData,
  (newData, oldData) => {
    if (newData && oldData && newData.microlearning !== oldData.microlearning) {
      forceUpdate.value++;
    }
  },
  { deep: true }
);

// Also watch for changes in the microlearning content specifically
watch(
  () => props.checkPointData?.microlearning,
  (newContent, oldContent) => {
    if (newContent !== oldContent) {
      nextTick(() => {
        forceUpdate.value++;
      });
    }
  }
);

const handleClick = (data) => {
  props.onClick(data);
};
</script>

<style scoped>
.microlearing-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 52px;
  width: 52px;
  cursor: url("../assets/cursorArrow.svg"), auto;
  position: relative;
}
.microlearing-icon:hover {
  background-color: #f2edff;
  box-shadow: 0px 4px 4px 0px #0000000f;
  border-radius: 8px;
}

.processing-indicator {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
}

.processing-dots {
  display: flex;
  gap: 2px;
}

.processing-dots span {
  width: 4px;
  height: 4px;
  background-color: #7f56d9;
  border-radius: 50%;
  animation: processing-pulse 1.4s infinite ease-in-out;
}

.processing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.processing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes processing-pulse {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
